/**
 * 轻量 JSON 编辑器：
 *   - 实时 JSON 高亮（key/string/number/bool/null）
 *   - JSON 解析错误用红色波浪线在出错行/列下标记
 *   - 可选 template，可一键重置为初始模板
 *
 * 实现：将彩色 <pre> 绝对定位在 textarea 后方，并同步滚动；textarea 自身设为透明前景。
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const COLORS = {
  key: "text-sky-700 dark:text-sky-300",
  string: "text-emerald-700 dark:text-emerald-300",
  number: "text-amber-700 dark:text-amber-300",
  bool: "text-violet-700 dark:text-violet-300",
  null: "text-rose-700 dark:text-rose-300",
  punct: "text-muted-foreground",
  comment: "text-muted-foreground italic",
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" } as any)[c]);
}

/** 剔除 JSONC 中的行注释 // ... 与块注释 /* ... *\/，保留字符串中的原始文本 */
export function stripJsonComments(s: string): string {
  let out = "";
  let i = 0;
  const n = s.length;
  let inStr = false;
  let strCh = "\"";
  while (i < n) {
    const c = s[i]!;
    const nx = s[i + 1];
    if (inStr) {
      out += c;
      if (c === "\\\\" && i + 1 < n) { out += s[i + 1]; i += 2; continue; }
      if (c === strCh) inStr = false;
      i++; continue;
    }
    if (c === "\"" || c === "'") { inStr = true; strCh = c; out += c; i++; continue; }
    if (c === "/" && nx === "/") {
      // 占位空格以保留位置（不影响错误偏移量计算）
      while (i < n && s[i] !== "\n") { out += " "; i++; }
      continue;
    }
    if (c === "/" && nx === "*") {
      out += "  ";
      i += 2;
      while (i < n && !(s[i] === "*" && s[i + 1] === "/")) {
        out += s[i] === "\n" ? "\n" : " ";
        i++;
      }
      if (i < n) { out += "  "; i += 2; }
      continue;
    }
    out += c; i++;
  }
  return out;
}

// ----------------------------------------------------------------------------
// JSONC 回填：以模板为骨架，将存量值覆盖到对应位置，保留模板中的注释
// ----------------------------------------------------------------------------
type ValueNode =
  | { kind: "object"; offset: number; end: number; members: { key: string; node: ValueNode }[] }
  | { kind: "array"; offset: number; end: number }
  | { kind: "primitive"; offset: number; end: number };

/** 解析 JSONC 字符串，返回带位置信息的值树（仅记录我们需要的层级信息） */
function parseJsoncValue(src: string, start: number): { node: ValueNode; next: number } {
  let i = skipWsComments(src, start);
  const c = src[i];
  if (c === "{") return parseObject(src, i);
  if (c === "[") return parseArray(src, i);
  return parsePrimitive(src, i);
}
function skipWsComments(src: string, i: number): number {
  const n = src.length;
  while (i < n) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
    if (c === "/" && src[i + 1] === "/") { while (i < n && src[i] !== "\n") i++; continue; }
    if (c === "/" && src[i + 1] === "*") {
      i += 2;
      while (i < n && !(src[i] === "*" && src[i + 1] === "/")) i++;
      if (i < n) i += 2;
      continue;
    }
    break;
  }
  return i;
}
function parseString(src: string, i: number): { value: string; next: number } {
  // 假设 src[i] === '"'
  const start = i; i++;
  while (i < src.length && src[i] !== "\"") {
    if (src[i] === "\\") i += 2; else i++;
  }
  i++; // 越过收尾的 "
  return { value: JSON.parse(src.slice(start, i)), next: i };
}
function parseObject(src: string, start: number): { node: ValueNode; next: number } {
  // src[start] === '{'
  let i = start + 1;
  const members: { key: string; node: ValueNode }[] = [];
  while (true) {
    i = skipWsComments(src, i);
    if (src[i] === "}") { i++; break; }
    // key
    if (src[i] !== "\"") throw new Error(`expected string key at ${i}`);
    const k = parseString(src, i); i = k.next;
    i = skipWsComments(src, i);
    if (src[i] !== ":") throw new Error(`expected : at ${i}`);
    i++;
    const v = parseJsoncValue(src, i); i = v.next;
    members.push({ key: k.value, node: v.node });
    i = skipWsComments(src, i);
    if (src[i] === ",") { i++; continue; }
    if (src[i] === "}") { i++; break; }
    throw new Error(`expected , or } at ${i}`);
  }
  return { node: { kind: "object", offset: start, end: i, members }, next: i };
}
function parseArray(src: string, start: number): { node: ValueNode; next: number } {
  let i = start + 1;
  while (true) {
    i = skipWsComments(src, i);
    if (src[i] === "]") { i++; break; }
    const v = parseJsoncValue(src, i); i = v.next;
    i = skipWsComments(src, i);
    if (src[i] === ",") { i++; continue; }
    if (src[i] === "]") { i++; break; }
    throw new Error(`expected , or ] at ${i}`);
  }
  return { node: { kind: "array", offset: start, end: i }, next: i };
}
function parsePrimitive(src: string, start: number): { node: ValueNode; next: number } {
  let i = start;
  if (src[i] === "\"") {
    const r = parseString(src, i); i = r.next;
  } else {
    const n = src.length;
    while (i < n && !/[\s,}\]\/]/.test(src[i]!)) i++;
  }
  return { node: { kind: "primitive", offset: start, end: i }, next: i };
}

/** 取得当前 offset 所在行的缩进字符串（用于多行 JSON 重新缩进） */
function lineIndent(src: string, offset: number): string {
  let lineStart = src.lastIndexOf("\n", offset - 1) + 1;
  let i = lineStart;
  while (i < src.length && (src[i] === " " || src[i] === "\t")) i++;
  return src.slice(lineStart, i);
}

/**
 * 以 JSONC 模板为骨架，把存量值（普通 JSON 对象）覆盖到对应位置，保留所有注释。
 * - 对象：按 key 匹配，递归覆盖；模板中没有的 key 追加到对象末尾
 * - 数组 / 类型不一致 / 叶子：整体替换为 JSON.stringify(value)（按所在行缩进重排）
 */
export function backfillJsoncWithValue(template: string, value: any): string {
  if (value === undefined || value === null) return template;
  let parsed: ValueNode;
  try { parsed = parseJsoncValue(template, 0).node; } catch { return template; }
  const edits: { offset: number; end: number; text: string }[] = [];
  const walk = (node: ValueNode, val: any) => {
    if (node.kind === "object" && val && typeof val === "object" && !Array.isArray(val)) {
      const seen = new Set<string>();
      for (const m of node.members) {
        seen.add(m.key);
        if (m.key in val) walk(m.node, val[m.key]);
      }
      // 追加模板里没有的 key
      const extras = Object.keys(val).filter((k) => !seen.has(k));
      if (extras.length > 0) {
        // 在 } 之前插入：找到 } 位置
        const closeIdx = node.end - 1; // src[closeIdx] === '}'
        const indent = "  " + lineIndent(template, node.offset);
        const before = template.slice(0, closeIdx).replace(/\s*$/, "");
        const needComma = before.length > 0 && before[before.length - 1] !== "{";
        const lines = extras.map((k) => `${indent}${JSON.stringify(k)}: ${reindentJson(val[k], indent)}`).join(",\n");
        edits.push({
          offset: closeIdx, end: closeIdx,
          text: (needComma ? "," : "") + "\n" + lines + "\n" + lineIndent(template, node.offset),
        });
      }
      return;
    }
    // 其他情况：整段替换
    const indent = lineIndent(template, node.offset);
    edits.push({ offset: node.offset, end: node.end, text: reindentJson(val, indent) });
  };
  walk(parsed, value);
  edits.sort((a, b) => b.offset - a.offset);
  let out = template;
  for (const e of edits) out = out.slice(0, e.offset) + e.text + out.slice(e.end);
  return out;
}

function reindentJson(v: any, indent: string): string {
  const txt = JSON.stringify(v, null, 2);
  if (!txt.includes("\n")) return txt;
  return txt.split("\n").map((l, i) => (i === 0 ? l : indent + l)).join("\n");
}

/** 计算编辑器初始文本：value 为空时显示 JSONC 模板；否则把 value 回填进模板，保留注释 */
function initialText(value: any, template: any): string {
  const isEmpty = value === undefined || value === null
    || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);
  if (typeof template === "string") {
    if (isEmpty) return template;
    return backfillJsoncWithValue(template, value);
  }
  return JSON.stringify(value ?? {}, null, 2);
}

/** 简易 JSONC 高亮：基于正则匹配 token（避免引入完整 parser） */
function highlight(src: string): string {
  if (!src) return "";
  const tokenRe = /(\/\/[^\n]*)|(\/\*[\s\S]*?\*\/)|("(?:\\.|[^"\\])*"\s*:?)|(\b-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|(\b(?:true|false)\b)|(\bnull\b)|([{}\[\],])/g;
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(src)) !== null) {
    if (m.index > last) out += escapeHtml(src.slice(last, m.index));
    const raw = m[0];
    if (m[1] || m[2]) {
      out += `<span class="${COLORS.comment}">${escapeHtml(raw)}</span>`;
    } else if (m[3]) {
      const endsWithColon = /:\s*$/.test(raw);
      const cls = endsWithColon ? COLORS.key : COLORS.string;
      out += `<span class="${cls}">${escapeHtml(raw)}</span>`;
    } else if (m[4]) {
      out += `<span class="${COLORS.number}">${escapeHtml(raw)}</span>`;
    } else if (m[5]) {
      out += `<span class="${COLORS.bool}">${escapeHtml(raw)}</span>`;
    } else if (m[6]) {
      out += `<span class="${COLORS.null}">${escapeHtml(raw)}</span>`;
    } else if (m[7]) {
      out += `<span class="${COLORS.punct}">${escapeHtml(raw)}</span>`;
    }
    last = m.index + raw.length;
  }
  if (last < src.length) out += escapeHtml(src.slice(last));
  if (src.endsWith("\n")) out += " ";
  return out;
}

/** 解析 SyntaxError.message 中的位置信息，返回 0-based offset */
function extractErrorPos(text: string, err: Error): number | null {
  const m = err.message.match(/position\s+(\d+)/i);
  if (m) return Math.max(0, Math.min(text.length, parseInt(m[1]!, 10)));
  const lc = err.message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
  if (lc) {
    const line = parseInt(lc[1]!, 10) - 1;
    const col = parseInt(lc[2]!, 10) - 1;
    const lines = text.split("\n");
    let pos = 0;
    for (let i = 0; i < line && i < lines.length; i++) pos += lines[i]!.length + 1;
    return pos + col;
  }
  return null;
}

export function JsonEditor({
  value, onChange, rows = 12, template, onReset,
}: {
  value: any;
  onChange: (v: any) => void;
  rows?: number;
  template?: any;
  onReset?: () => void;
}) {
  const [text, setText] = useState(() => initialText(value, template));
  const [err, setErr] = useState<{ msg: string; pos: number | null } | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);

  // 外部 value 变化（如重置、切换条目）时刷新文本
  useEffect(() => {
    try {
      const cur = JSON.parse(stripJsonComments(text));
      if (JSON.stringify(cur) !== JSON.stringify(value ?? {})) {
        setText(initialText(value, template));
      }
    } catch {
      setText(initialText(value, template));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const html = useMemo(() => highlight(text), [text]);

  /** 错误下波浪线：在文本上方覆盖一层 absolute 的 span */
  const squiggle = useMemo(() => {
    if (!err || err.pos == null) return null;
    const before = text.slice(0, err.pos);
    const lineIdx = (before.match(/\n/g) || []).length;
    const lineStart = before.lastIndexOf("\n") + 1;
    const col = err.pos - lineStart;
    return { lineIdx, col };
  }, [err, text]);

  const handleChange = (v: string) => {
    setText(v);
    try {
      onChange(JSON.parse(stripJsonComments(v)));
      setErr(null);
    } catch (er: any) {
      setErr({ msg: er.message, pos: extractErrorPos(stripJsonComments(v), er) });
    }
  };

  const syncScroll = () => {
    const ta = taRef.current, pre = preRef.current;
    if (!ta || !pre) return;
    pre.scrollTop = ta.scrollTop;
    pre.scrollLeft = ta.scrollLeft;
  };

  const reset = () => {
    if (template === undefined) return;
    const t = typeof template === "string" ? template : JSON.stringify(template, null, 2);
    setText(t);
    try { onChange(JSON.parse(stripJsonComments(t))); } catch {}
    setErr(null);
    onReset?.();
  };

  // 行号
  const lines = text.split("\n").length;

  return (
    <div className="space-y-1">
      <div className="relative rounded-md border bg-background overflow-hidden">
        {/* 彩色层（背景） */}
        <pre
          ref={preRef}
          aria-hidden="true"
          className="absolute inset-0 m-0 p-3 pl-12 font-mono text-xs leading-5 whitespace-pre overflow-auto pointer-events-none text-foreground"
          style={{ tabSize: 2 }}
          dangerouslySetInnerHTML={{ __html: html || " " }}
        />
        {/* 行号 */}
        <pre
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-10 m-0 py-3 px-2 font-mono text-[10px] leading-5 text-right text-muted-foreground/60 bg-muted/30 select-none border-r overflow-hidden"
        >
          {Array.from({ length: lines }, (_, i) => i + 1).join("\n")}
        </pre>
        {/* 编辑层（透明文字） */}
        <textarea
          ref={taRef}
          rows={rows}
          spellCheck={false}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onScroll={syncScroll}
          className="relative block w-full p-3 pl-12 font-mono text-xs leading-5 bg-transparent text-transparent caret-foreground resize-y outline-none whitespace-pre"
          style={{ tabSize: 2 }}
        />
        {/* 错误波浪线 */}
        {squiggle && (
          <div
            aria-hidden="true"
            className="absolute pointer-events-none ztb-json-squiggle"
            style={{
              top: `calc(0.75rem + ${squiggle.lineIdx} * 1.25rem)`,
              left: `calc(3rem + ${squiggle.col}ch)`,
              width: "1.5ch",
              height: "1.25rem",
            }}
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex-1 min-h-[1rem]">
          {err ? (
            <span className="text-rose-600">JSON 解析错误：{err.msg}</span>
          ) : (
            <span className="text-emerald-600">✓ JSON 格式正确</span>
          )}
        </div>
        {template !== undefined && (
          <Button type="button" size="sm" variant="ghost" onClick={reset}
            className="h-7 px-2 text-xs">
            <RotateCcw className="h-3 w-3 mr-1" /> 重置为初始模板
          </Button>
        )}
      </div>
      <style>{`
        .ztb-json-squiggle {
          background-image: linear-gradient(45deg, transparent 25%, hsl(var(--destructive, 0 84% 60%)) 25%, hsl(var(--destructive, 0 84% 60%)) 50%, transparent 50%, transparent 75%, hsl(var(--destructive, 0 84% 60%)) 75%);
          background-size: 4px 4px;
          background-position: 0 100%;
          background-repeat: repeat-x;
          height: 4px !important;
          top: auto !important;
          margin-top: 1.05rem;
        }
      `}</style>
    </div>
  );
}
